import {ActionList, ActionMenu} from '@primer/react'

import {
  DEFAULT_REASONING_EFFORT,
  getReasoningEffortMenuLabel,
  REASONING_EFFORT_OPTIONS,
  type ReasoningEffort,
} from '../../utils/reasoning-effort'
import styles from './ReasoningEffortPicker.module.css'

interface ReasoningEffortPickerProps {
  selectedReasoningEffort?: ReasoningEffort
  onUpdateReasoningEffort: (reasoningEffort: ReasoningEffort | undefined) => void
  // The reasoning efforts the selected model supports
  supportedReasoningEfforts: readonly string[] | undefined
}

export function ReasoningEffortPicker({
  selectedReasoningEffort,
  onUpdateReasoningEffort,
  supportedReasoningEfforts,
}: ReasoningEffortPickerProps) {
  // Hide until we know the model supports reasoning
  if (!supportedReasoningEfforts || supportedReasoningEfforts.length === 0) {
    return null
  }

  const options = REASONING_EFFORT_OPTIONS.filter(option => supportedReasoningEfforts.includes(option.value))

  // No explicit selection falls back to the backend default (medium).
  const effortValue = selectedReasoningEffort ?? DEFAULT_REASONING_EFFORT

  return (
    <ActionMenu>
      <ActionMenu.Button
        className={styles.menuButton}
        variant="invisible"
        aria-label={`Reasoning effort: ${getReasoningEffortMenuLabel(effortValue)}`}
      >
        <span className={styles.buttonName}>{getReasoningEffortMenuLabel(effortValue)}</span>
      </ActionMenu.Button>
      <ActionMenu.Overlay width="small" align="end">
        <ActionList selectionVariant="single">
          <div className={styles.menuHeading}>Reasoning effort</div>
          {options.map(option => (
            <ActionList.Item
              key={option.value}
              selected={option.value === effortValue}
              onSelect={() =>
                onUpdateReasoningEffort(option.value === DEFAULT_REASONING_EFFORT ? undefined : option.value)
              }
            >
              {getReasoningEffortMenuLabel(option.value)}
              <ActionList.Description variant="block">{option.description}</ActionList.Description>
            </ActionList.Item>
          ))}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
